import type { EnKey } from "odyssey-lang";

/**
 * The format of a vfx definition (ADR-134 decisions 2 and 3): a named visual effect as data, a list of timed layers and
 * a reduced-motion form. A definition holds no drawing and no colour; the surface draws its layers (`world-ui/vfx/`) in
 * the tone set the player was given. Times are milliseconds from the start, sizes are art pixels, and an art pixel is one
 * pixel of the world's art.
 */

/**
 * The most rays a vfx draws, and the most sparks.
 */
export const MAX_RAYS = 12;
export const MAX_SPARKS = 24;

/**
 * A ring flat on the ground at the feet, its radius going from `radiusFrom` to `radiusTo` between `fromMs` and `toMs`,
 * in the bright tone or the dark.
 */
export interface RingLayer {
  readonly kind: "ring";
  readonly fromMs: number;
  readonly toMs: number;
  readonly radiusFrom: number;
  readonly radiusTo: number;
  readonly bright: boolean;
}

/**
 * A dithered haze over the body. `keys` are `[ms, strength]` points the strength moves between in straight lines; two
 * keys at one time make a jump; the layer ends at its last key. `peak` is the strength that is the strongest step.
 */
export interface AuraLayer {
  readonly kind: "aura";
  readonly keys: readonly (readonly [number, number])[];
  readonly peak: number;
}

/**
 * A pillar of light on the feet, `half` art pixels either side of its middle: it grows to the top of the screen in
 * `growMs` from `fromMs`, holds its width to `holdUntilMs`, then narrows in three steps and goes at `endMs`.
 */
export interface PillarLayer {
  readonly kind: "pillar";
  readonly fromMs: number;
  readonly growMs: number;
  readonly holdUntilMs: number;
  readonly endMs: number;
  readonly half: number;
}

/**
 * `count` rays spread round the middle of the body for `spanMs` from `fromMs`, their inner and outer ends going from the
 * first number to the second.
 */
export interface RaysLayer {
  readonly kind: "rays";
  readonly fromMs: number;
  readonly spanMs: number;
  readonly count: number;
  readonly inner: readonly [number, number];
  readonly outer: readonly [number, number];
}

/**
 * One spark: it starts `delayMs` after the layer and flies for `lifeMs` in a straight line from `(x0, y0)` to `(x1, y1)`
 * in art pixels from the middle of the body, in the light tone or the mid.
 */
export interface SparkPath {
  readonly delayMs: number;
  readonly lifeMs: number;
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
  readonly light: 0 | 1;
}

/**
 * Sparks, running for `spanMs` from `fromMs`. A spark's plus has two arms until the first fraction of its life, one until
 * the second and is a dot after.
 */
export interface SparksLayer {
  readonly kind: "sparks";
  readonly fromMs: number;
  readonly spanMs: number;
  readonly armsUntil: readonly [number, number];
  readonly paths: readonly SparkPath[];
}

/**
 * A sign over the head with a word from the language files, rising `risePx` in `riseMs` from `fromMs`, shown to `toMs`,
 * dark for the `gap` of two times when there is one (a single blink).
 */
export interface TextLayer {
  readonly kind: "text";
  readonly word: EnKey;
  readonly fromMs: number;
  readonly riseMs: number;
  readonly risePx: number;
  readonly toMs: number;
  readonly gap: readonly [number, number] | null;
}

/**
 * A glow at the screen's edges, in quarter steps, for the page's own character only.
 */
export interface EdgeLayer {
  readonly kind: "edge";
  readonly fromMs: number;
  readonly toMs: number;
}

/**
 * A bright band that runs along the XP bar from its left end to its right `runs` times, evenly, between `fromMs` and
 * `toMs`, `widthPx` logical pixels wide. For a HUD target.
 */
export interface WaveLayer {
  readonly kind: "wave";
  readonly fromMs: number;
  readonly toMs: number;
  readonly widthPx: number;
  readonly runs: number;
}

/**
 * The bar's pulse: a wash over the whole bar, its strength moving between `[ms, strength]` keys in straight lines, a step
 * of `PULSE_STEPS` at `peak`. For a HUD target.
 */
export interface PulseLayer {
  readonly kind: "pulse";
  readonly keys: readonly (readonly [number, number])[];
  readonly peak: number;
}

/**
 * One crackle off the bar: it starts `delayMs` after the layer, lives `lifeMs`, stands `at` of the way along the bar (0 to
 * 1), rises `rise` logical pixels (negative is up) while it falls back `fall` pixels by the end (so it flies in an arc),
 * drifts `drift` across, is `size` logical pixels square (1 to 4), in the light tone or the mid.
 */
export interface Crackle {
  readonly delayMs: number;
  readonly lifeMs: number;
  readonly at: number;
  readonly rise: number;
  readonly drift: number;
  readonly fall: number;
  readonly size: 1 | 2 | 3 | 4;
  readonly light: 0 | 1;
}

/**
 * Small sparks that crackle off the bar's edge, running for `spanMs` from `fromMs`. For a HUD target.
 */
export interface CrackleLayer {
  readonly kind: "crackle";
  readonly fromMs: number;
  readonly spanMs: number;
  readonly crackles: readonly Crackle[];
}

/**
 * One layer.
 */
export type VfxLayer = RingLayer | AuraLayer | PillarLayer | RaysLayer | SparksLayer | TextLayer | EdgeLayer | WaveLayer | PulseLayer | CrackleLayer;

/**
 * What a vfx is drawn on: a character in the world, or the HUD's XP bar.
 */
export type VfxTargetKind = "character" | "xpBar";

/**
 * The layer kinds each target draws.
 */
export const TARGET_LAYERS: Readonly<Record<VfxTargetKind, readonly VfxLayer["kind"][]>> = Object.freeze({
  character: ["ring", "aura", "pillar", "rays", "sparks", "text", "edge"],
  xpBar: ["wave", "pulse", "crackle"],
});

/**
 * The most crackles a bar vfx draws.
 */
export const MAX_CRACKLES = 64;

/**
 * How many steps the bar's pulse has, and so its strongest.
 */
export const PULSE_STEPS = 4;

/**
 * What a vfx is under reduced motion: a still flash for `flashMs` (the pillar at `pillarHalf`, 0 for none; the aura, the
 * edge glow and the bar's pulse at their steps), the text still for the rest of `lifeMs` when `textStill`, and no ring, ray, spark,
 * growth, narrowing or blink.
 */
export interface ReducedForm {
  readonly lifeMs: number;
  readonly flashMs: number;
  readonly pillarHalf: number;
  readonly auraStep: number;
  readonly edgeStep: number;
  readonly pulseStep: number;
  readonly textStill: boolean;
}

/**
 * The sound hooks a vfx plays: for the page's own character and, placed, for another player's; null for none.
 */
export interface VfxSound {
  readonly own: string | null;
  readonly others: string | null;
}

/**
 * A vfx definition.
 */
export interface VfxDefinition {
  readonly id: string;
  readonly lifeMs: number;
  readonly target: VfxTargetKind;
  readonly others: boolean;
  readonly sound: VfxSound;
  readonly layers: readonly VfxLayer[];
  readonly reduced: ReducedForm;
}

/**
 * The first layer of a kind.
 *
 * @param definition The definition.
 * @param kind The kind.
 * @returns The layer, or undefined when the definition has none.
 */
export function layerOf<K extends VfxLayer["kind"]>(definition: VfxDefinition, kind: K): Extract<VfxLayer, { kind: K }> | undefined {
  for (let index = 0; index < definition.layers.length; index += 1) {
    const layer = definition.layers[index] as VfxLayer;
    if (layer.kind === kind) {
      return layer as Extract<VfxLayer, { kind: K }>;
    }
  }
  return undefined;
}

/**
 * Checks a definition.
 *
 * @param definition The definition.
 * @returns The problems found, none for a good definition.
 */
export function validateVfx(definition: VfxDefinition): string[] {
  const problems: string[] = [];
  const life = definition.lifeMs;
  const within = (what: string, ...times: number[]): void => {
    for (const time of times) {
      if (!Number.isFinite(time) || time < 0 || time > life) {
        problems.push(`${definition.id}: ${what} names ${time} ms, outside its life of ${life} ms`);
      }
    }
  };
  if (!/^[a-z][A-Za-z0-9]*$/.test(definition.id)) {
    problems.push(`${definition.id}: the id is not a camel-case word`);
  }
  if (!(life > 0)) {
    problems.push(`${definition.id}: its life is not above 0`);
  }
  for (const hook of [definition.sound.own, definition.sound.others]) {
    if (hook !== null && !hook.startsWith("sfx/") && !hook.startsWith("stingers/")) {
      problems.push(`${definition.id}: the sound hook ${hook} lies outside sfx/ and stingers/`);
    }
  }
  const seen = new Set<string>();
  for (const layer of definition.layers) {
    if (layer.kind === "ring") {
      within("a ring", layer.fromMs, layer.toMs);
      if (!(layer.toMs > layer.fromMs) || layer.radiusFrom < 2 || layer.radiusTo < 2) {
        problems.push(`${definition.id}: a ring runs backwards or has a radius under 2`);
      }
    } else if (layer.kind === "aura") {
      within("the aura", ...layer.keys.map((key) => key[0]));
      if (layer.keys.length < 2 || !(layer.peak > 0)) {
        problems.push(`${definition.id}: the aura needs two keys and a peak above 0`);
      }
      for (let index = 1; index < layer.keys.length; index += 1) {
        if ((layer.keys[index] as readonly number[])[0] as number < ((layer.keys[index - 1] as readonly number[])[0] as number)) {
          problems.push(`${definition.id}: the aura's keys go back in time`);
        }
      }
    } else if (layer.kind === "pillar") {
      within("the pillar", layer.fromMs, layer.holdUntilMs, layer.endMs);
      if (!(layer.growMs > 0) || !(layer.holdUntilMs >= layer.fromMs) || !(layer.endMs > layer.holdUntilMs) || layer.half < 1) {
        problems.push(`${definition.id}: the pillar's times are out of order or it has no width`);
      }
    } else if (layer.kind === "rays") {
      within("the rays", layer.fromMs, layer.fromMs + layer.spanMs);
      if (layer.count < 1 || layer.count > MAX_RAYS || !(layer.spanMs > 0)) {
        problems.push(`${definition.id}: the rays number ${layer.count}, over the ${MAX_RAYS} drawn, or have no span`);
      }
    } else if (layer.kind === "sparks") {
      within("the sparks", layer.fromMs, layer.fromMs + layer.spanMs);
      if (layer.paths.length < 1 || layer.paths.length > MAX_SPARKS) {
        problems.push(`${definition.id}: the sparks number ${layer.paths.length}, over the ${MAX_SPARKS} drawn`);
      }
      for (const path of layer.paths) {
        if (!(path.lifeMs > 0) || path.delayMs < 0 || path.delayMs + path.lifeMs > layer.spanMs) {
          problems.push(`${definition.id}: a spark outlives its layer's span`);
        }
      }
      if (!(layer.armsUntil[0] > 0 && layer.armsUntil[0] <= layer.armsUntil[1])) {
        problems.push(`${definition.id}: the sparks' arm fractions are out of order`);
      }
    } else if (layer.kind === "text") {
      within("the text", layer.fromMs, layer.toMs, ...(layer.gap ?? []));
      if (!(layer.toMs > layer.fromMs) || layer.riseMs <= 0 || layer.word.length === 0) {
        problems.push(`${definition.id}: the text runs backwards, never rises or names no word`);
      }
    } else if (layer.kind === "edge") {
      within("the edge glow", layer.fromMs, layer.toMs);
      if (!(layer.toMs > layer.fromMs)) {
        problems.push(`${definition.id}: the edge glow runs backwards`);
      }
    } else if (layer.kind === "wave") {
      within("the wave", layer.fromMs, layer.toMs);
      if (!(layer.toMs > layer.fromMs) || !(layer.widthPx >= 1) || !Number.isInteger(layer.runs) || layer.runs < 1) {
        problems.push(`${definition.id}: the wave runs backwards, has no width or has no whole number of runs`);
      }
    } else if (layer.kind === "pulse") {
      within("the pulse", ...layer.keys.map((key) => key[0]));
      if (layer.keys.length < 2 || !(layer.peak > 0)) {
        problems.push(`${definition.id}: the pulse needs two keys and a peak above 0`);
      }
    } else {
      within("the crackle", layer.fromMs, layer.fromMs + layer.spanMs);
      if (layer.crackles.length < 1 || layer.crackles.length > MAX_CRACKLES) {
        problems.push(`${definition.id}: the crackles number ${layer.crackles.length}, over the ${MAX_CRACKLES} drawn`);
      }
      for (const crackle of layer.crackles) {
        if (!(crackle.lifeMs > 0) || crackle.delayMs < 0 || crackle.delayMs + crackle.lifeMs > layer.spanMs || crackle.at < 0 || crackle.at > 1 || !(crackle.size >= 1 && crackle.size <= 4)) {
          problems.push(`${definition.id}: a crackle outlives its layer's span, stands off the bar or has a size outside 1 to 4`);
        }
      }
    }
    if (!TARGET_LAYERS[definition.target].includes(layer.kind)) {
      problems.push(`${definition.id}: a ${layer.kind} layer cannot be drawn on ${definition.target}`);
    }
    if (seen.has(layer.kind) && layer.kind !== "ring") {
      problems.push(`${definition.id}: has two ${layer.kind} layers; only rings may repeat`);
    }
    seen.add(layer.kind);
  }
  const reduced = definition.reduced;
  if (!(reduced.lifeMs > 0) || reduced.flashMs > reduced.lifeMs || reduced.lifeMs > life) {
    problems.push(`${definition.id}: its reduced form is longer than its life or its flash outlasts it`);
  }
  if (reduced.pillarHalf > 0 && layerOf(definition, "pillar") === undefined) {
    problems.push(`${definition.id}: its reduced form has a pillar the definition lacks`);
  }
  if (reduced.edgeStep > 0 && layerOf(definition, "edge") === undefined) {
    problems.push(`${definition.id}: its reduced form has an edge glow the definition lacks`);
  }
  if (reduced.pulseStep > 0 && layerOf(definition, "pulse") === undefined) {
    problems.push(`${definition.id}: its reduced form has a pulse the definition lacks`);
  }
  if (reduced.textStill && layerOf(definition, "text") === undefined) {
    problems.push(`${definition.id}: its reduced form has a text the definition lacks`);
  }
  return problems;
}
